import { useTranslations } from 'next-intl'
import type { ReactNode } from 'react'
import type { PricingPlanFeatureKey } from '@/constants/pricing.constants'

type RichTranslationValues = Record<string, (chunks: ReactNode) => ReactNode>

/**
 * Hook for accessing pricing feature translations with dynamic keys.
 * Handles optional properties (tag, tooltip) that may not exist on all features.
 *
 * Note: Dynamic template literal keys cannot be fully type-checked by TypeScript
 * when combined with next-intl's strict typing. This hook provides runtime safety
 * through PricingPlanFeatureKey validation while working around TypeScript limitations.
 */
export function usePricingFeatureTranslations() {
	const t = useTranslations('pricingPlanFeatures')

	return {
		getLabel: (key: PricingPlanFeatureKey): string => {
			return t(`${key}.label` as never)
		},

		getTooltip: (key: PricingPlanFeatureKey): string => {
			return t(`${key}.tooltip` as never)
		},

		getTag: (key: PricingPlanFeatureKey): string => {
			return t(`${key}.tag` as never)
		},

		hasTooltip: (key: PricingPlanFeatureKey): boolean => {
			return t.has(`${key}.tooltip` as never)
		},

		hasTag: (key: PricingPlanFeatureKey): boolean => {
			return t.has(`${key}.tag` as never)
		},

		richLabel: (key: PricingPlanFeatureKey, values?: RichTranslationValues): ReactNode => {
			return t.rich(`${key}.label` as never, values as never)
		},

		rawTooltip: (key: PricingPlanFeatureKey): string => {
			return t.raw(`${key}.tooltip` as never)
		},
	}
}
